/*
 * a singleton cache for the paymaster session authorizations
 * (see libs/paymaster/sponsorshipAuthorization.ts for what they are and why
 * they are session-scoped rather than per-userOp)
 */
import {
  SPONSORSHIP_SESSION_REFRESH_MARGIN_SECONDS,
  SponsorshipAuthorization
} from '../../libs/paymaster/sponsorshipAuthorization'

// In-memory only, on purpose. Losing it (extension service worker restart, app
// reload) costs at most one extra signature — silent for a hot key, one device
// confirmation for a hardware wallet — whereas persisting it would put a
// replayable account-scoped token on disk for no real gain.
export class SponsorshipSessions {
  #sessions: { [cacheKey: string]: SponsorshipAuthorization } = {}

  // eslint-disable-next-line class-methods-use-this
  #getCacheKey(chainId: bigint, account: string, key: string): string {
    return `${chainId.toString()}:${account.toLowerCase()}:${key.toLowerCase()}`
  }

  get(chainId: bigint, account: string, key: string): SponsorshipAuthorization | null {
    const session = this.#sessions[this.#getCacheKey(chainId, account, key)]
    if (!session) return null

    const nowInSeconds = Math.floor(Date.now() / 1000)
    if (session.expiresAt - SPONSORSHIP_SESSION_REFRESH_MARGIN_SECONDS <= nowInSeconds) {
      this.remove(chainId, account, key)
      return null
    }

    return session
  }

  set(chainId: bigint, session: SponsorshipAuthorization) {
    this.#sessions[this.#getCacheKey(chainId, session.account, session.key)] = session
  }

  remove(chainId: bigint, account: string, key: string) {
    delete this.#sessions[this.#getCacheKey(chainId, account, key)]
  }

  removeAll() {
    this.#sessions = {}
  }
}

export const sponsorshipSessions = new SponsorshipSessions()
